/* Glacier — global.js
   Vanilla JS, no dependencies. Loaded with `defer`.
   Handles: cart drawer + AJAX cart, header state, mobile nav,
   hover prefetch, reveal-on-scroll. */
(() => {
  "use strict";

  const routes = JSON.parse(document.getElementById("RoutesData").textContent);

  /* ---------- Cart drawer ---------- */
  const drawer = document.getElementById("CartDrawer");

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    const closeBtn = drawer.querySelector("[data-drawer-close]");
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }

  async function refreshCart() {
    // Section Rendering API: re-render only the drawer section + get cart count
    const [sectionRes, cartRes] = await Promise.all([
      fetch(`${routes.cart_url}?sections=cart-drawer`),
      fetch("/cart.js"),
    ]);
    const sections = await sectionRes.json();
    const cart = await cartRes.json();

    const html = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
    const fresh = html.getElementById("CartDrawer");
    if (fresh && drawer) drawer.innerHTML = fresh.innerHTML;

    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = cart.item_count > 0 ? cart.item_count : "";
    });
  }

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-drawer-open]")) {
      e.preventDefault();
      openDrawer();
    }
    if (e.target.closest("[data-drawer-close]") || e.target.classList.contains("cart-drawer__overlay")) {
      closeDrawer();
    }
    const removeBtn = e.target.closest("[data-line-remove]");
    if (removeBtn) {
      e.preventDefault();
      changeLine(removeBtn.dataset.lineRemove, 0);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDrawer();
  });

  async function changeLine(line, quantity) {
    await fetch(routes.cart_change_url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line: Number(line), quantity }),
    });
    await refreshCart();
    // If we're on the cart page, reload to keep it in sync
    if (document.body.classList.contains("template-cart")) window.location.reload();
  }

  /* Quantity steppers (drawer + cart page + product) */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-qty-change]");
    if (!btn) return;
    const wrap = btn.closest(".qty-input");
    const input = wrap.querySelector("input");
    const step = btn.dataset.qtyChange === "up" ? 1 : -1;
    const min = Number(input.min || 0);
    const next = Math.max(min, Number(input.value || 0) + step);
    input.value = next;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* Line quantity change (inputs inside drawer/cart marked with data-line) */
  document.addEventListener("change", (e) => {
    const input = e.target.closest("input[data-line]");
    if (!input) return;
    changeLine(input.dataset.line, Number(input.value));
  });

  /* ---------- AJAX add to cart ---------- */
  document.addEventListener("submit", async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const label = submitBtn ? submitBtn.textContent : "";
    if (submitBtn) {
      submitBtn.setAttribute("aria-disabled", "true");
      submitBtn.textContent = "Adding…";
    }
    try {
      const res = await fetch(routes.cart_add_url + ".js", {
        method: "POST",
        body: new FormData(form),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.description || "Could not add to cart.");
        return;
      }
      await refreshCart();
      openDrawer();
    } finally {
      if (submitBtn) {
        submitBtn.removeAttribute("aria-disabled");
        submitBtn.textContent = label;
      }
    }
  });

  /* ---------- Header scroll state ---------- */
  const header = document.querySelector(".site-header");
  if (header) {
    let ticking = false;
    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          header.classList.toggle("is-scrolled", window.scrollY > 12);
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  /* ---------- Navigation drawer ---------- */
  // Drill-down panes: active pane shows; leaving pane slides left (forward) or right (back)
  const switchPane = (drawer, id, isBack) => {
    drawer.querySelectorAll(".nav-drawer__pane").forEach((pane) => {
      if (pane.dataset.pane === id) {
        pane.classList.remove("is-left");
        pane.classList.add("is-active");
        pane.scrollTop = 0;
      } else if (pane.classList.contains("is-active")) {
        pane.classList.remove("is-active");
        pane.classList.toggle("is-left", !isBack);
      }
    });
  };

  const setNavOpen = (open) => {
    const navDrawer = document.getElementById("NavDrawer");
    if (!navDrawer) return;
    navDrawer.classList.toggle("is-open", open);
    navDrawer.setAttribute("aria-hidden", String(!open));
    document.body.style.overflow = open ? "hidden" : "";
    document.querySelectorAll("[data-nav-toggle]").forEach((btn) => btn.setAttribute("aria-expanded", String(open)));
    if (open) {
      // Always start at level 1
      navDrawer.querySelectorAll(".nav-drawer__pane").forEach((pane) => {
        pane.classList.toggle("is-active", pane.dataset.pane === "root");
        pane.classList.remove("is-left");
      });
      navDrawer.querySelector("[data-nav-close]")?.focus();
    }
  };

  document.addEventListener("click", (e) => {
    const paneBtn = e.target.closest("[data-pane-target]");
    const backBtn = e.target.closest("[data-pane-back]");
    if (paneBtn || backBtn) {
      const drawer = (paneBtn || backBtn).closest(".nav-drawer");
      const id = paneBtn ? paneBtn.dataset.paneTarget : backBtn.dataset.paneBack;
      if (drawer && id) switchPane(drawer, id, !!backBtn);
      return;
    }
    if (e.target.closest("[data-nav-toggle]")) {
      setNavOpen(true);
    } else if (
      e.target.closest("[data-nav-close]") ||
      e.target.classList.contains("nav-drawer__overlay")
    ) {
      setNavOpen(false);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setNavOpen(false);
  });

  /* ---------- Predictive search ---------- */
  const searchModal = document.getElementById("SearchModal");
  if (searchModal) {
    const input = document.getElementById("SearchInput");
    const resultsEl = document.getElementById("SearchResults");
    const suggestUrl = resultsEl.dataset.searchUrl || "/search/suggest";
    const searchPage = resultsEl.dataset.searchPage || "/search";
    let searchController = null;
    let searchDebounce = null;

    const setSearchOpen = (open) => {
      searchModal.classList.toggle("is-open", open);
      searchModal.setAttribute("aria-hidden", String(!open));
      document.body.style.overflow = open ? "hidden" : "";
      if (open) {
        setNavOpen(false); // close the menu drawer if it was open
        document.body.style.overflow = "hidden";
        setTimeout(() => input.focus(), 60);
      }
    };

    document.addEventListener("click", (e) => {
      const toggle = e.target.closest("[data-search-toggle]");
      if (toggle) {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (e.target.closest("[data-search-close]") || e.target.classList.contains("search-modal__overlay")) {
        setSearchOpen(false);
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") setSearchOpen(false);
    });

    const esc = (s) =>
      String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    // Full Shopify money-format support (amount_no_decimals, comma separators, …)
    const fmtMoney = (amount) => {
      const fmt = resultsEl.dataset.moneyFormat || "${{amount}}";
      const n = parseFloat(amount) || 0;
      const keyMatch = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      const key = keyMatch ? keyMatch[1] : "amount";
      const num = (precision, thousands, decimal) => {
        const parts = n.toFixed(precision).split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };
      let value;
      switch (key) {
        case "amount_no_decimals": value = num(0, ",", "."); break;
        case "amount_with_comma_separator": value = num(2, ".", ","); break;
        case "amount_no_decimals_with_comma_separator": value = num(0, ".", ","); break;
        case "amount_with_apostrophe_separator": value = num(2, "'", "."); break;
        case "amount_no_decimals_with_space_separator": value = num(0, " ", ","); break;
        case "amount_with_space_separator": value = num(2, " ", ","); break;
        case "amount_with_period_and_space_separator": value = num(2, " ", "."); break;
        default: value = num(2, ",", ".");
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    };
    const thumb = (url) => (url ? url + (url.includes("?") ? "&" : "?") + "width=120" : "");

    const renderResults = (r, q) => {
      const parts = [];
      if (r.products && r.products.length) {
        parts.push('<p class="search-modal__group-title">Products</p>');
        r.products.forEach((p) => {
          const price = parseFloat(p.price);
          const compare = parseFloat(p.compare_at_price_max || p.compare_at_price_min || 0);
          const priceHtml =
            compare > price
              ? `<s class="search-result__compare">${fmtMoney(compare.toFixed(2))}</s> <span class="price__sale">${fmtMoney(price.toFixed(2))}</span>`
              : fmtMoney(esc(p.price));
          parts.push(
            `<a class="search-result" href="${esc(p.url)}">` +
              (p.image ? `<img src="${esc(thumb(p.image))}" alt="" width="48" height="48" loading="lazy">` : "") +
              `<span class="search-result__text"><span class="search-result__title">${esc(p.title)}</span>` +
              `<span class="search-result__meta">${priceHtml}</span></span></a>`
          );
        });
      }
      if (r.collections && r.collections.length) {
        parts.push('<p class="search-modal__group-title">Collections</p>');
        r.collections.forEach((c) => {
          parts.push(`<a class="search-result" href="${esc(c.url)}"><span class="search-result__text"><span class="search-result__title">${esc(c.title)}</span></span></a>`);
        });
      }
      if (r.pages && r.pages.length) {
        parts.push('<p class="search-modal__group-title">Pages</p>');
        r.pages.forEach((p) => {
          parts.push(`<a class="search-result" href="${esc(p.url)}"><span class="search-result__text"><span class="search-result__title">${esc(p.title)}</span></span></a>`);
        });
      }
      if (!parts.length) {
        parts.push(`<p class="search-modal__empty">No results for &ldquo;${esc(q)}&rdquo;</p>`);
      }
      parts.push(`<a class="search-modal__all" href="${esc(searchPage)}?q=${encodeURIComponent(q)}">View all results &rarr;</a>`);
      resultsEl.innerHTML = parts.join("");
    };

    input.addEventListener("input", () => {
      clearTimeout(searchDebounce);
      const q = input.value.trim();
      if (q.length < 2) {
        resultsEl.innerHTML = "";
        return;
      }
      searchDebounce = setTimeout(async () => {
        if (searchController) searchController.abort();
        searchController = new AbortController();
        try {
          const res = await fetch(
            `${suggestUrl}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last`,
            { signal: searchController.signal }
          );
          if (!res.ok) return;
          const data = await res.json();
          renderResults(data.resources.results, q);
        } catch (err) {
          /* aborted or offline — keep previous results */
        }
      }, 250);
    });
  }

  /* ---------- Collection facets: click-outside / Escape closes ---------- */
  document.addEventListener("click", (e) => {
    document.querySelectorAll("details.facet[open]").forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute("open");
    });
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      document.querySelectorAll("details.facet[open]").forEach((d) => d.removeAttribute("open"));
    }
  });

  /* ---------- Hover prefetch (near-instant navigation) ---------- */
  const prefetched = new Set();
  function prefetch(url) {
    if (prefetched.has(url) || prefetched.size > 40) return;
    prefetched.add(url);
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
  }
  document.addEventListener(
    "mouseover",
    (e) => {
      const a = e.target.closest("a[href]");
      if (!a) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname) return;
      if (/\/(cart|checkout|account)/.test(url.pathname)) return;
      prefetch(url.pathname + url.search);
    },
    { passive: true }
  );

  /* ---------- Carousels & banner sliders ---------- */
  const TRACK_SELECTOR = ".carousel__track, .banner-slider__track";

  // Width of one item (banner sliders) or one page (full-width carousels)
  const slideStep = (track) => {
    if (track.classList.contains("carousel__track")) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const first = track.firstElementChild;
    return (first ? first.getBoundingClientRect().width : track.clientWidth) + gap;
  };

  // Native smooth scrolling is unreliable on mandatory-snap containers
  // (Chromium can cancel it entirely), so animate scrollLeft ourselves.
  const glideTo = (track, left) => {
    const max = track.scrollWidth - track.clientWidth;
    const target = Math.max(0, Math.min(left, max));
    if (track._glideRaf) cancelAnimationFrame(track._glideRaf);
    clearTimeout(track._glideGuard);
    const done = () => {
      track._glideRaf = null;
      track.style.scrollSnapType = "";
      if (track._normalize) track._normalize(); // looping sliders: recenter after gliding
    };
    const from = track.scrollLeft;
    const dist = target - from;
    if (Math.abs(dist) < 1 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      track.scrollLeft = target;
      done();
      return;
    }
    const duration = 380;
    const t0 = performance.now();
    let started = false;
    track.style.scrollSnapType = "none"; // snap fights the animation
    const frame = (now) => {
      started = true;
      const p = Math.min(1, (now - t0) / duration);
      const ease = 1 - Math.pow(1 - p, 3); // ease-out cubic
      track.scrollLeft = from + dist * ease;
      if (p < 1) track._glideRaf = requestAnimationFrame(frame);
      else done();
    };
    track._glideRaf = requestAnimationFrame(frame);
    // Watchdog: if frames are throttled (hidden tab, some webviews), jump instantly
    track._glideGuard = setTimeout(() => {
      if (!started) {
        cancelAnimationFrame(track._glideRaf);
        track.scrollLeft = target;
        done();
      }
    }, 120);
  };

  // Arrows + dots via delegation — keeps working when the theme editor re-renders sections
  document.addEventListener("click", (e) => {
    const prev = e.target.closest("[data-carousel-prev], [data-slider-prev]");
    const next = e.target.closest("[data-carousel-next], [data-slider-next]");
    const dot = e.target.closest("[data-carousel-dot]");
    if (!prev && !next && !dot) return;
    const root = (prev || next || dot).closest("[data-carousel], [data-slider]");
    const track = root && root.querySelector(TRACK_SELECTOR);
    if (!track) return;

    if (dot) {
      glideTo(track, track.clientWidth * Number(dot.dataset.carouselDot));
    } else if (root.hasAttribute("data-carousel")) {
      // Full-width slides: wrap around at the ends
      const count = track.children.length;
      const i = Math.round(track.scrollLeft / track.clientWidth) + (next ? 1 : -1);
      glideTo(track, track.clientWidth * ((i + count) % count));
    } else {
      if (track._normalize) track._normalize(); // looping sliders: recenter first
      const step = slideStep(track);
      const target = (Math.round(track.scrollLeft / step) + (next ? 1 : -1)) * step;
      glideTo(track, target);
    }
  });

  // Mouse drag-to-scroll: makes tracks "swipeable" with a mouse on desktop.
  // (Touch swiping is native scroll — untouched.)
  let dragTrack = null;
  let dragStartX = 0;
  let dragStartScroll = 0;
  let dragged = false;

  document.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const track = e.target.closest(TRACK_SELECTOR);
    if (!track) return;
    dragTrack = track;
    dragStartX = e.clientX;
    dragStartScroll = track.scrollLeft;
    dragged = false;
    track.style.scrollSnapType = "none"; // snap fights manual scrolling
    track.style.cursor = "grabbing";
  });

  document.addEventListener("pointermove", (e) => {
    if (!dragTrack) return;
    const dx = e.clientX - dragStartX;
    if (Math.abs(dx) > 6) dragged = true;
    if (dragged) dragTrack.scrollLeft = dragStartScroll - dx;
  });

  const endDrag = () => {
    if (!dragTrack) return;
    const track = dragTrack;
    dragTrack = null;
    track.style.cursor = "";
    if (dragged) {
      if (track._normalize) track._normalize(); // looping sliders: recenter first
      const step = slideStep(track);
      glideTo(track, Math.round(track.scrollLeft / step) * step);
    } else {
      track.style.scrollSnapType = "";
    }
  };
  document.addEventListener("pointerup", endDrag);
  document.addEventListener("pointercancel", endDrag);

  // After a real drag, swallow the click so banner links don't fire
  document.addEventListener(
    "click",
    (e) => {
      if (dragged && e.target.closest(TRACK_SELECTOR)) {
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      }
    },
    true
  );
  // Stop native image/link dragging from hijacking the swipe
  document.addEventListener("dragstart", (e) => {
    if (e.target.closest(TRACK_SELECTOR)) e.preventDefault();
  });

  // Per-carousel state: dot highlighting + autoplay (re-initable for the theme editor)
  function initCarousel(carousel) {
    if (carousel.dataset.inited) return;
    carousel.dataset.inited = "true";
    const track = carousel.querySelector(".carousel__track");
    if (!track || track.children.length < 2) return;
    const dots = [...carousel.querySelectorAll("[data-carousel-dot]")];
    const current = () => Math.round(track.scrollLeft / track.clientWidth);

    let scrollTick = false;
    track.addEventListener(
      "scroll",
      () => {
        if (scrollTick) return;
        scrollTick = true;
        requestAnimationFrame(() => {
          const i = current();
          dots.forEach((dot, d) => dot.setAttribute("aria-current", d === i ? "true" : "false"));
          scrollTick = false;
        });
      },
      { passive: true }
    );

    const delay = Number(carousel.dataset.autoplay || 0);
    if (delay > 0 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      let timer = null;
      const count = track.children.length;
      const start = () => {
        if (!timer) timer = setInterval(() => {
          glideTo(track, track.clientWidth * ((current() + 1) % count));
        }, delay);
      };
      const stop = () => { clearInterval(timer); timer = null; };
      carousel.addEventListener("mouseenter", stop);
      carousel.addEventListener("mouseleave", start);
      carousel.addEventListener("touchstart", stop, { passive: true });
      document.addEventListener("visibilitychange", () => (document.hidden ? stop() : start()));
      start();
    }
  }
  document.querySelectorAll("[data-carousel]").forEach(initCarousel);

  /* ---------- Continuous (infinite) loop for banner/product sliders ----------
     Three identical item sets sit in the track; scrolling stays inside the middle
     set by silently jumping ±1 set width at the boundaries (identical content,
     so the jump is invisible). */
  const initLoop = (slider) => {
    const track = slider.querySelector(".banner-slider__track");
    if (!track || track.dataset.looped) return;
    const items = [...track.children];
    // Loop only when the content actually overflows
    if (!items.length || track.scrollWidth <= track.clientWidth + 10) return;
    track.dataset.looped = "true";

    const makeClones = () =>
      items.map((el) => {
        const clone = el.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.classList.add("is-visible");
        clone.classList.remove("reveal");
        clone.querySelectorAll(".reveal").forEach((r) => r.classList.add("is-visible"));
        if (clone.matches("a, button, input, select, [tabindex]")) clone.tabIndex = -1;
        clone.querySelectorAll("a, button, input, select, [tabindex]").forEach((f) => (f.tabIndex = -1));
        return clone;
      });

    makeClones().forEach((c) => track.appendChild(c));
    const front = makeClones();
    for (let i = front.length - 1; i >= 0; i--) track.insertBefore(front[i], track.firstChild);

    const n = items.length;
    // Stride = exact pixel distance between one set and the next (handles gaps)
    const stride = () => track.children[n].offsetLeft - track.children[0].offsetLeft;

    // Recenter into the middle band so both directions always have room.
    // Exposed on the track so arrow clicks / drag-release can call it directly.
    track._normalize = () => {
      const w = stride();
      if (track.scrollLeft < w * 0.5) track.scrollLeft += w;
      else if (track.scrollLeft > w * 1.5) track.scrollLeft -= w;
    };

    // Start on the middle (original) set, without triggering a visible jump
    track.style.scrollSnapType = "none";
    track.scrollLeft = stride();
    track.style.scrollSnapType = "";

    // Normalize after native touch/trackpad scrolling too
    track.addEventListener(
      "scroll",
      () => {
        // Don't fight an in-flight glide animation or an active mouse drag
        if (track._glideRaf || dragTrack === track) return;
        track._normalize();
      },
      { passive: true }
    );
  };
  document.querySelectorAll("[data-slider]").forEach(initLoop);

  document.addEventListener("shopify:section:load", (e) => {
    e.target.querySelectorAll("[data-carousel]").forEach(initCarousel);
    e.target.querySelectorAll("[data-slider]").forEach(initLoop);
  });

  /* ---------- Collection: infinite scroll / load more ---------- */
  const initLoadMore = (root) => {
    const holder = root.querySelector("[data-load-more]");
    const grid = root.querySelector("[data-collection-grid]");
    if (!holder || !grid || holder.dataset.inited) return;
    holder.dataset.inited = "true";
    const btn = holder.querySelector("[data-load-more-btn]");
    const spinner = holder.querySelector(".load-more__spinner");
    let loading = false;

    const loadNext = async () => {
      if (loading || !holder.dataset.nextUrl) return;
      loading = true;
      if (spinner) spinner.hidden = false;
      if (btn) btn.setAttribute("aria-disabled", "true");
      try {
        const res = await fetch(holder.dataset.nextUrl);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        const newGrid = doc.querySelector("[data-collection-grid]");
        if (newGrid) {
          [...newGrid.children].forEach((card) => {
            card.classList.add("is-visible");
            card.querySelectorAll(".reveal").forEach((r) => r.classList.add("is-visible"));
            grid.appendChild(card);
          });
        }
        const nextHolder = doc.querySelector("[data-load-more]");
        if (nextHolder && nextHolder.dataset.nextUrl) {
          holder.dataset.nextUrl = nextHolder.dataset.nextUrl;
        } else {
          holder.dataset.nextUrl = "";
          holder.remove(); // last page reached
        }
      } catch (e) {
        /* network hiccup — user can retry via button/scroll */
      } finally {
        loading = false;
        if (spinner) spinner.hidden = true;
        if (btn) btn.removeAttribute("aria-disabled");
      }
    };

    if (btn) btn.addEventListener("click", loadNext);
    if (holder.dataset.mode === "infinite") {
      if ("IntersectionObserver" in window) {
        const io = new IntersectionObserver(
          (entries) => { if (entries[0].isIntersecting) loadNext(); },
          { rootMargin: "900px 0px" }
        );
        io.observe(holder);
      } else if (btn) {
        btn.hidden = false; // no IO support → degrade to a Load more button
      }
    }
  };
  initLoadMore(document);
  document.addEventListener("shopify:section:load", (e) => initLoadMore(e.target));

  /* ---------- Reveal on scroll ---------- */
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const revealEls = document.querySelectorAll(".reveal");
  if (revealEls.length && !reduceMotion && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" }
    );
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add("is-visible"));
  }
})();
